'use client';

import { config, useSpring, animated } from '@react-spring/web';
import Link from 'next/link';
import { Tooltip } from 'tdesign-react';
import { useEffect, useRef, useState, useMemo, use } from 'react';
import { CaretRightSmallIcon } from 'tdesign-icons-react';
import { useSectionCardStore } from '../section-card';
import { useToolDocument } from '../tool-document';
import { useScroll } from '@use-gesture/react';

interface IMenuItemProps {
  title: string;
  href?: string;
  active?: boolean;
  level?: number;
  onClick?(): void;
  showArrow?: boolean;
  isOpen?: boolean;
  onOpen?(): void;
  hidden?: boolean;
}

interface IMenuProps {
  children: React.ReactNode;
}

function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number = 100
): T {
  let timeoutId: ReturnType<typeof setTimeout>;
  return ((...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), wait);
  }) as T;
}

const useRightMenu = (alwayShow: boolean = false) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const styles = useSpring({
    from: {
      opacity: 0,
      display: 'none',
    },
    to: {
      opacity: isSticky || alwayShow ? 1 : 0,
      display: isSticky || alwayShow ? 'block' : 'none',
    },
    config: config.slow,
  });

  const bind = useScroll(
    ({ active, xy: [, y] }) => {
      if (!active) {
        const top = ref.current?.getBoundingClientRect().top || 0;
        setIsSticky(y > top + 75 + 46 + 50);
      }
    },
    { axis: 'y' }
  );

  useEffect(() => {
    const { onScroll } = bind();
    const handleScroll = debounce((e) => {
      onScroll?.(e);
    }, 10);

    window.addEventListener('scroll', onScroll as any);

    return () => {
      window.removeEventListener('scroll', onScroll as any);
    };
  });

  return {
    ref,
    styles,
  };
};

export const MenuItem: React.FC<IMenuItemProps> = ({
  title,
  href,
  active = false,
  level = 1,
  onClick,
  showArrow,
  isOpen,
  onOpen,
  hidden,
}) => {
  const liContent = (
    <Tooltip
      content={title}
      destroyOnClose
      showArrow
      placement="right"
      overlayClassName="max-w-96"
    >
      <span
        onClick={onClick}
        className={`${
          active ? 'text-primary' : 'text-primary/[.6]'
        } text-sm leading-[22px] line-clamp-1 w-[160px] cursor-pointer`}
      >
        {title}
      </span>
    </Tooltip>
  );
  return (
    <li
      className={`${
        hidden ? 'hidden' : ''
      } h-8 flex items-center rounded hover:bg-btn ${
        active ? 'bg-btn font-500' : ''
      }`}
      style={{
        paddingLeft: level * 8,
        paddingRight: level * 8,
      }}
    >
      {showArrow && (
        <CaretRightSmallIcon
          color={active ? 'rgba(0,0,0,0.9)' : 'rgba(0,0,0,0.6)'}
          className={`w-4 h-4 cursor-pointer mr-2 ${
            isOpen ? 'rotate-90' : 'rotate-0'
          }`}
          onClick={onOpen}
        />
      )}
      {href ? <Link href={href}>{liContent}</Link> : liContent}
    </li>
  );
};

export const Menu: React.FC<IMenuProps> = ({ children }) => {
  return (
    <ul className="border-border border border-solid bg-white/[.9] rounded-lg p-2">
      {children}
    </ul>
  );
};

const menu = {
  intro: '工具介绍',
  inventory: '申领清单',
  learn: '案例教程',
  note: '注意事项',
  'digital-service': '什么是数字化服务',
};
let scrollTimer: any;

export const RightMenu = () => {
  const { styles, ref } = useRightMenu();
  const { ids } = useSectionCardStore();
  const idArr = Object.keys(menu);
  const items = idArr
    .map((key) => {
      if (ids.hasOwnProperty(key)) return key;
      return null;
    })
    .filter((f) => f !== null) as string[];
  const actIndex = items.findIndex((f) => ids[f]);
  const active = actIndex === -1 ? 0 : actIndex;
  const setInView = useSectionCardStore((s) => s.setInView);

  useEffect(() => {
    if (!items.length) return;
    window.addEventListener('scroll', function () {
      // 当开始滚动时，清除之前的定时器
      clearTimeout(scrollTimer);
      // 设置一个新的定时器，如果在150ms内没有滚动，则认为滚动结束
      scrollTimer = setTimeout(function () {
        // 滚动结束了
        const elIds = items;
        const tops: any[] = [];
        elIds.forEach((d) => {
          const el = document.getElementById(d);
          if (el) {
            const y = el?.getClientRects()[0].y;
            tops.push(y);
            setInView(d, false);
          }
        });
        let actId = elIds[0];

        if (tops.every((v) => v < 0)) {
          actId = elIds.at(-1) ?? elIds[0];
        }
        if (tops.every((v) => v > 0)) {
          actId = elIds[0];
        }
        if (tops.some((v) => v > 0)) {
          const i = tops.findIndex((v, i) => v > 0);
          actId = elIds[i];
        }
        setInView(actId, true);
      }, 150);
    });
  }, [items]);
  const onIntoView = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth' });
    items.forEach((d) => {
      if (d !== id) setInView(d, false);
    });
    setInView(id, true);
  };
  return (
    <>
      {items.length !== 0 ? (
        <animated.div
          ref={ref}
          className="sticky pl-[30px] top-[118px] w-[216px] box-content"
        >
          <Menu>
            {items.map((item, i) => (
              <MenuItem
                key={i}
                title={menu[item as keyof typeof menu]}
                active={active === i}
                onClick={() => onIntoView(item)}
                // href={}
              />
            ))}
          </Menu>
        </animated.div>
      ) : null}
    </>
  );
};

export const RightDocumentMenu = ({
  alwayShow = false,
}: {
  alwayShow?: boolean;
}) => {
  const { headers } = useToolDocument();
  // const { styles, ref } = useRightMenu(alwayShow);
  const [activeKey, setActiveKey] = useState(() =>
    headers.length ? headers[0].id : ''
  );
  const enableRef = useRef(true);
  const [open, setOpen] = useState<Array<string>>([]);
  useEffect(() => {
    if (!headers || !headers.length) return;

    const selections: (HTMLElement | null)[] = headers.map((v) => {
      return document.getElementById(`${v.id}`);
    });
    const navHeightLight = () => {
      if (!enableRef.current) return;
      let scrollY = window.scrollY;
      selections.forEach((f: HTMLElement | null) => {
        if (f) {
          const sectionHeight = f.offsetHeight;
          const sectionTop =
            f.getBoundingClientRect().top + window.scrollY - 50;
          if (scrollY > sectionTop && scrollY < sectionTop + sectionHeight) {
            setActiveKey(f.id);
          }
        }
      });
    };

    window.addEventListener('scroll', navHeightLight);
    return () => {
      window.removeEventListener('scroll', navHeightLight);
    };
  }, [headers]);

  const handleClick = (key: string) => {
    setActiveKey(key);
    enableRef.current = false;
    document
      .getElementById(key)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    setTimeout(() => {
      enableRef.current = true;
    }, 500);
  };

  const handleOpen = (key: string) => {
    setOpen((p) =>
      p.includes(key) ? p.filter((f) => f !== key) : [...p, key]
    );
  };

  const headerTree = useMemo(() => {
    return headers.reduce((p: any, n, index) => {
      return [
        ...p,
        {
          ...n,
          findParent: (
            headers
              .slice(0, index)
              .reverse()
              .find((f) => f.level < n.level && n.level !== 1) || {}
          ).id,
        },
      ];
    }, []);
  }, [headers]);

  useEffect(() => {
    setOpen(headerTree.map((item: any) => item.findParent).filter(Boolean));
  }, [JSON.stringify(headerTree)]);

  return !headerTree.length ? null : (
    <animated.div
      // ref={ref}
      className="sticky pl-[30px] top-[118px] w-[216px] box-content"
      // style={styles}
    >
      <Menu>
        {headerTree.map((m: any, i: number) => {
          return (
            <MenuItem
              key={i}
              title={m.text}
              active={m.id == activeKey}
              level={m.level}
              showArrow={!m.findParent}
              isOpen={open.includes(m.id)}
              onOpen={() => handleOpen(m.id)}
              hidden={m.findParent && !open.includes(m.findParent)}
              onClick={() => handleClick(m.id)}
            />
          );
        })}
      </Menu>
    </animated.div>
  );
};
